import React, { useEffect, useState } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

interface BoxPlotItem {
  experiment_id: string;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  outliers?: number[];
  count?: number;
}

interface BoxPlotProps {
  data: BoxPlotItem[];
  title: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  height?: number;
  scaleFactor?: number;
  boxWidth?: number;
  boxColors?: {
    fill: string;
    stroke: string;
    medianStroke: string;
    whiskerStroke: string;
    outliersColor: string;
  };
}

const validateBoxPlotData = (data: BoxPlotItem[]): BoxPlotItem[] => {
  return data.map((item) => {
    const numericItem = {
      ...item,
      min: Number(item.min),
      q1: Number(item.q1),
      median: Number(item.median),
      q3: Number(item.q3),
      max: Number(item.max),
      outliers: item.outliers ? item.outliers.map((v) => Number(v)) : [],
    };

    let { min, q1, median, q3, max } = numericItem;

    const orderError =
      min > q1 ||
      q1 > median ||
      median > q3 ||
      q3 > max ||
      isNaN(min) ||
      isNaN(q1) ||
      isNaN(median) ||
      isNaN(q3) ||
      isNaN(max);

    if (orderError) {
      const values = [min, q1, median, q3, max].filter((v) => !isNaN(v));

      if (values.length === 0) {
        return {
          ...numericItem,
          min: 0,
          q1: 0,
          median: 0,
          q3: 0,
          max: 0,
          outliers: [],
        };
      }

      values.sort((a, b) => a - b);

      if (values.length === 1) {
        const val = values[0];
        return {
          ...numericItem,
          min: val - 0.2,
          q1: val - 0.1,
          median: val,
          q3: val + 0.1,
          max: val + 0.2,
        };
      } else if (values.length < 5) {
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        const range = maxVal - minVal;

        return {
          ...numericItem,
          min: minVal,
          q1: minVal + range * 0.25,
          median: minVal + range * 0.5,
          q3: minVal + range * 0.75,
          max: maxVal,
        };
      } else {
        return {
          ...numericItem,
          min: values[0],
          q1: values[1],
          median: values[2],
          q3: values[3],
          max: values[4],
        };
      }
    }

    return numericItem;
  });
};

const BoxPlotBox = (props: any) => {
  const { cx, cy, payload, yAxis, boxWidth, boxColors } = props;

  if (!payload || !yAxis || !cx || !cy) return null;

  const getYCoord = (value: number): number => {
    if (typeof yAxis.scale === "function") {
      return yAxis.scale(value);
    }
    return cy;
  };

  const q1Y = getYCoord(payload.q1);
  const q3Y = getYCoord(payload.q3);
  const medianY = getYCoord(payload.median);
  const minY = getYCoord(payload.min);
  const maxY = getYCoord(payload.max);

  const boxHeight = Math.abs(q3Y - q1Y);
  const boxY = Math.min(q1Y, q3Y);

  const boxX = cx - boxWidth / 2;

  const minBoxHeight = 2;
  const actualBoxHeight = Math.max(minBoxHeight, boxHeight);

  const actualBoxY =
    boxHeight < minBoxHeight ? medianY - minBoxHeight / 2 : boxY;

  return (
    <g>
      <Line
        x1={cx}
        y1={maxY}
        x2={cx}
        y2={minY}
        stroke={boxColors.whiskerStroke}
        strokeWidth={1}
      />

      <Line
        x1={boxX}
        y1={maxY}
        x2={boxX + boxWidth}
        y2={maxY}
        stroke={boxColors.whiskerStroke}
        strokeWidth={1}
      />

      <Line
        x1={boxX}
        y1={minY}
        x2={boxX + boxWidth}
        y2={minY}
        stroke={boxColors.whiskerStroke}
        strokeWidth={1}
      />

      <Rectangle
        x={boxX}
        y={actualBoxY}
        width={boxWidth}
        height={actualBoxHeight}
        fill={boxColors.fill}
        stroke={boxColors.stroke}
        strokeWidth={1}
      />

      <Line
        x1={boxX}
        y1={medianY}
        x2={boxX + boxWidth}
        y2={medianY}
        stroke={boxColors.medianStroke}
        strokeWidth={2}
      />
    </g>
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  scaleFactor = 1,
}: TooltipProps<ValueType, NameType> & { scaleFactor?: number }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    if (payload[0].name === "Outlier") {
      const outlierValue = (Number(data.value) * scaleFactor).toFixed(2);
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
          }}
        >
          <p>{`Experiment ID: ${data.experiment_id}`}</p>
          <p
            style={{ color: payload[0].color }}
          >{`Outlier: ${outlierValue}`}</p>
        </div>
      );
    }

    if (data.min !== undefined) {
      const format = (value: number) =>
        (Number(value) * scaleFactor).toFixed(2);
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
          }}
        >
          <p>{`Exp ID: ${label}`}</p>
          <p>{`Max: ${format(data.max)}`}</p>
          <p>{`Q3: ${format(data.q3)}`}</p>
          <p>{`Med: ${format(data.median)}`}</p>
          <p>{`Q1: ${format(data.q1)}`}</p>
          <p>{`Min: ${format(data.min)}`}</p>
          {data.count && <p>{`Counts: ${data.count}`}</p>}
        </div>
      );
    }
  }

  return null;
};

const BoxPlot: React.FC<BoxPlotProps> = ({
  data,
  title,
  xAxisLabel = "",
  yAxisLabel = "Value",
  height = 400,
  scaleFactor = 1,
  boxWidth = 35,
  boxColors = {
    fill: "#8884d870",
    stroke: "#8884d8",
    medianStroke: "#4834cb",
    whiskerStroke: "#8884d8",
    outliersColor: "#ff7300",
  },
}) => {
  const [processedData, setProcessedData] = useState<BoxPlotItem[]>([]);
  const [outliers, setOutliers] = useState<any[]>([]);
  const [yDomain, setYDomain] = useState<[number, number]>([0, 1]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setProcessedData([]);
      setOutliers([]);
      setYDomain([0, 1]);
      return;
    }

    const validatedData = validateBoxPlotData(data);

    const allValues: number[] = [];
    validatedData.forEach((item) => {
      const itemValues = [
        item.min,
        item.q1,
        item.median,
        item.q3,
        item.max,
      ].filter((v) => !isNaN(v));
      allValues.push(...itemValues);
      if (item.outliers) {
        allValues.push(...item.outliers.filter((v) => !isNaN(v)));
      }
    });

    let minValue = 0;
    let maxValue = 1;

    if (allValues.length > 0) {
      minValue = Math.min(...allValues);
      maxValue = Math.max(...allValues);
    } else {
    }

    const padding = (maxValue - minValue) * 0.1 || 0.1;

    const outlierData: any[] = [];
    validatedData.forEach((item, index) => {
      if (item.outliers && item.outliers.length > 0) {
        item.outliers.forEach((value) => {
          if (!isNaN(value)) {
            outlierData.push({
              experiment_id: item.experiment_id,
              value: value,
            });
          }
        });
      }
    });

    const finalMin = isFinite(minValue - padding) ? minValue - padding : 0;
    const finalMax = isFinite(maxValue + padding) ? maxValue + padding : 1;

    setYDomain([finalMin, finalMax]);
    setProcessedData(validatedData);
    setOutliers(outlierData);
  }, [data]);

  if (!processedData || processedData.length === 0) {
    return (
      <div>
        <h3>{title}</h3>
        <div
          style={{
            height: `${height}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Error: No Data Available
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3>{title}</h3>
      <div style={{ width: "100%", height: `${height}px` }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={processedData}
            margin={{ top: 20, right: 50, left: 30, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
              dataKey="experiment_id"
              type="category"
              allowDuplicatedCategory={false}
              angle={-45}
              textAnchor="end"
              interval={0}
              padding={{ left: 30, right: 30 }}
              height={80}
              fontSize={12}
              label={
                xAxisLabel
                  ? { value: xAxisLabel, position: "insideBottom", offset: -15 }
                  : undefined
              }
            />

            <YAxis
              yAxisId="left"
              domain={yDomain}
              tickFormatter={(value) =>
                (Number(value) * scaleFactor).toFixed(1)
              }
              allowDataOverflow={true}
              label={
                yAxisLabel
                  ? {
                      value: yAxisLabel,
                      angle: -90,
                      position: "insideLeft",
                      style: { textAnchor: "middle" },
                    }
                  : undefined
              }
            />

            <Tooltip content={<CustomTooltip scaleFactor={scaleFactor} />} />

            {/* <Legend /> */}

            <Scatter
              yAxisId="left"
              name="BoxPlot"
              dataKey="median"
              shape={<BoxPlotBox boxWidth={boxWidth} boxColors={boxColors} />}
              data={processedData}
            />

            {outliers.length > 0 && (
              <Scatter
                yAxisId="left"
                name="outlier"
                dataKey="value"
                data={outliers}
                fill={boxColors.outliersColor}
                shape="circle"
                legendType="circle"
                xAxisId={0}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BoxPlot;
